import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguageCode,
} from '../i18n/languages';
import { localizedAlternates, localizedUrl, normalizeLanguageCode } from '../seo/localizedUrl';
import { PageSeo } from '../seo/routes';
import { SITE_NAME, absoluteOgImageUrl } from '../seo/site';

const JSON_LD_ID = 'spotdove-json-ld';
const HREFLANG_MARKER = 'data-spotdove-hreflang';
const LOCALE_ALT_MARKER = 'data-spotdove-og-locale-alt';

const upsertMeta = (attribute: 'name' | 'property', key: string, content: string): void => {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  );

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const setOgLocaleAlternates = (locales: string[]): void => {
  document.head
    .querySelectorAll(`meta[property="og:locale:alternate"][${LOCALE_ALT_MARKER}]`)
    .forEach((element) => element.remove());

  for (const locale of locales) {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:locale:alternate');
    meta.setAttribute('content', locale);
    meta.setAttribute(LOCALE_ALT_MARKER, 'true');
    document.head.appendChild(meta);
  }
};

const upsertLink = (rel: string, href: string, extra?: Record<string, string>): void => {
  const selectorParts = [`link[rel="${rel}"]`];
  if (extra) {
    for (const [attr, value] of Object.entries(extra)) {
      selectorParts.push(`[${attr}="${value}"]`);
    }
  }

  let element = document.head.querySelector<HTMLLinkElement>(selectorParts.join(''));

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    if (extra) {
      for (const [attr, value] of Object.entries(extra)) {
        element.setAttribute(attr, value);
      }
    }
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const setHreflangLinks = (path: string, index: boolean): void => {
  document.head
    .querySelectorAll(`link[rel="alternate"][hreflang][${HREFLANG_MARKER}]`)
    .forEach((element) => element.remove());

  if (!index) {
    return;
  }

  for (const alternate of localizedAlternates(path)) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alternate.hreflang;
    link.href = alternate.href;
    link.setAttribute(HREFLANG_MARKER, 'true');
    document.head.appendChild(link);
  }

  const defaultLink = document.createElement('link');
  defaultLink.rel = 'alternate';
  defaultLink.hreflang = 'x-default';
  defaultLink.href = localizedUrl(path, DEFAULT_LANGUAGE);
  defaultLink.setAttribute(HREFLANG_MARKER, 'true');
  document.head.appendChild(defaultLink);
};

const setJsonLd = (data: PageSeo['jsonLd']): void => {
  const existing = document.getElementById(JSON_LD_ID);
  existing?.remove();

  if (!data) {
    return;
  }

  const script = document.createElement('script');
  script.id = JSON_LD_ID;
  script.type = 'application/ld+json';
  script.text = JSON.stringify(Array.isArray(data) ? data : [data]);
  document.head.appendChild(script);
};

interface PageMetaProps {
  seo: PageSeo;
}

const PageMeta: React.FC<PageMetaProps> = ({ seo }) => {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguageCode(i18n.resolvedLanguage) as SupportedLanguageCode;
  const canonicalUrl = useMemo(() => localizedUrl(seo.path, language), [seo.path, language]);
  const ogImage = useMemo(() => absoluteOgImageUrl(), []);
  const robots = seo.index === false ? 'noindex, nofollow' : 'index, follow';
  const ogType = seo.ogType ?? 'website';
  const currentLocale =
    SUPPORTED_LANGUAGES.find((entry) => entry.code === language)?.ogLocale ?? 'en_US';
  const alternateLocales = SUPPORTED_LANGUAGES.filter((entry) => entry.code !== language).map(
    (entry) => entry.ogLocale,
  );

  useEffect(() => {
    document.title = seo.title;

    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'robots', robots);
    upsertMeta('name', 'application-name', SITE_NAME);

    upsertLink('canonical', canonicalUrl);
    setHreflangLinks(seo.path, seo.index !== false);

    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', currentLocale);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:alt', t('seo.ogImageAlt'));
    setOgLocaleAlternates(alternateLocales);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', ogImage);

    setJsonLd(seo.jsonLd);

    return () => {
      document.head
        .querySelectorAll(`link[rel="alternate"][hreflang][${HREFLANG_MARKER}]`)
        .forEach((element) => element.remove());
      document.head
        .querySelectorAll(`meta[property="og:locale:alternate"][${LOCALE_ALT_MARKER}]`)
        .forEach((element) => element.remove());
    };
  }, [
    seo,
    canonicalUrl,
    ogImage,
    robots,
    ogType,
    currentLocale,
    alternateLocales,
    t,
  ]);

  return null;
};

export default PageMeta;
